import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { SITE } from "../data/menu";
import { Flourish, Blossom, PetalField, HeartMotif } from "../components/decor";
import { Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";

const VALUES = [
  { title: "Pure Vegetarian", text: "Not a rule — a promise. Every gravy, every grain, every garnish." },
  { title: "Family First", text: "High chairs, patient smiles, and food that pleases grandmother and grandchild alike." },
  { title: "Blooming Décor", text: "Pink walls, blossoms overhead, golden lanterns — dine inside a flower." },
  { title: "Honest Prices", text: "Festival flavours at everyday prices, so celebration never waits." },
];

export default function About() {
  return (
    <main data-testid="about-page">
      <section className="relative overflow-hidden px-5 pb-14 pt-20 text-center md:pt-28">
        <PetalField count={8} />
        <Reveal>
          <p className="text-xs font-medium uppercase tracking-[0.3em] text-[#C9A227]">Our Story</p>
          <h1 className="mt-3 font-serif text-5xl font-black text-[#9B2C2C] md:text-6xl">Rooted in Dumka</h1>
          <div className="mt-5 flex justify-center"><Flourish className="h-4 w-56" /></div>
        </Reveal>
      </section>

      <section className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-24 md:px-10 lg:grid-cols-2 lg:gap-20">
        <Reveal>
          <div className="relative">
            <div className="absolute -inset-3 rounded-[18px] border border-[#C9A227]/40" aria-hidden="true" />
            <div className="overflow-hidden rounded-[12px] shadow-[0_24px_60px_rgba(155,44,44,0.16)]">
              <motion.img
                src="/images/interior-04.jpg"
                alt="Bloomora dining floor — bougainvillea canopy, neon sign and marble family tables"
                data-testid="about-restaurant-image"
                className="aspect-[4/5] w-full object-cover sm:aspect-[4/3]"
                initial={{ scale: 1.12 }}
                whileInView={{ scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              />
            </div>
            <div className="absolute -bottom-6 -right-4 rounded-[12px] bg-[#9B2C2C] px-6 py-4 text-white shadow-xl md:-right-8">
              <p className="font-serif text-2xl font-bold">The Sweet Spot</p>
              <p className="text-xs uppercase tracking-[0.2em] text-[#E8B4B8]">&amp; Savouries</p>
            </div>
          </div>
        </Reveal>

        <div>
          <Reveal>
            <h2 className="font-serif text-3xl font-bold leading-tight text-[#9B2C2C] md:text-4xl">
              A family restaurant where every meal blooms
            </h2>
            <Flourish className="mt-4 h-4 w-40" />
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-6 text-base leading-relaxed text-[#2C2C2C] md:text-lg">
              On Bhagalpur Road, just by Dharmsthan Mandir, stands a little pink building that smells of
              tandoor smoke and cardamom. That is <span className="font-serif italic text-[#9B2C2C]">Bloomora</span> —
              Dumka's pure vegetarian, multi-cuisine family restaurant.
            </p>
            <p className="mt-4 text-base leading-relaxed text-[#2C2C2C] md:text-lg">
              We cook the way families eat: generously. Twelve chapters of a menu book that travels from
              South Indian dosas to Kolkata's Tangra lanes, from dum biryani to wood-fired style pizza —
              all under one ceiling of blossoms. Come hungry, leave blooming.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link
                to="/menu"
                data-testid="about-menu-button"
                className="group relative rounded-full bg-[#9B2C2C] px-8 py-3 font-medium text-white transition-colors hover:bg-[#7E2222]"
              >
                See Our Menu
                <span className="absolute bottom-2 left-1/2 h-[2px] w-0 -translate-x-1/2 bg-[#C9A227] transition-[width] duration-300 group-hover:w-1/2" />
              </Link>
              <a
                href={SITE.phoneHref}
                data-testid="about-call-button"
                className="rounded-full border-2 border-[#C9A227] px-8 py-2.5 font-medium text-[#9B2C2C] transition-colors hover:bg-[#C9A227] hover:text-white"
              >
                Call {SITE.phone}
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Marquee />

      <section className="mx-auto max-w-7xl px-5 py-24 md:px-10">
        <Reveal className="text-center">
          <h2 className="font-serif text-3xl font-bold text-[#9B2C2C] md:text-4xl">What We Hold Dear</h2>
          <div className="mt-4 flex justify-center"><Flourish className="h-4 w-48" /></div>
        </Reveal>
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4 md:gap-8">
          {VALUES.map((v, i) => (
            <Reveal key={v.title} delay={i * 0.08}>
              <div
                data-testid={`about-value-${i}`}
                className="group relative h-full overflow-hidden rounded-[12px] border border-[#E8B4B8]/30 bg-white/80 p-7 pb-9 shadow-[0_6px_24px_rgba(155,44,44,0.07)] backdrop-blur-sm transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-[0_18px_44px_rgba(201,162,39,0.2)]"
              >
                <Blossom className="h-8 w-8 text-[#E8B4B8] transition-colors duration-500 group-hover:text-[#C9A227]" />
                <h3 className="mt-4 font-serif text-xl font-semibold text-[#9B2C2C]">{v.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-[#B86B77]">{v.text}</p>
                <div className="absolute bottom-2.5 left-1/2 -translate-x-1/2 text-[#E8B4B8]/70">
                  <HeartMotif className="h-3.5 w-3.5" />
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </main>
  );
}
